import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
  Loader2, Mail, ShieldCheck, AlertCircle, BookOpen, Send, BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// Push-style Z-close certificate email settings.
// Default ON: tras cada Corte-Z se envía el certificado JSON firmado
// (SHA-256) al destinatario efectivo (owner.email > override > accountant).
// El owner puede apagarlo o redirigir a otra cuenta.

export default function ZCertEmailSettingsCard({ restaurantId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [override, setOverride] = useState("");
  const [bookOverride, setBookOverride] = useState("");
  const [sendingBook, setSendingBook] = useState(false);
  const [monthlyOverride, setMonthlyOverride] = useState("");
  const [sendingMonthly, setSendingMonthly] = useState(false);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const s = await api.getZCertEmailSettings(restaurantId);
      setSettings(s);
      setOverride(s.override_to || "");
      setBookOverride(s.annual_book?.override_to || "");
      setMonthlyOverride(s.monthly_exec_summary?.override_to || "");
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { z_cert_email_enabled: next });
      setSettings(s);
      toast.success(next ? "Envío automático activado" : "Envío automático desactivado");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const saveOverride = async () => {
    const trimmed = override.trim();
    if (trimmed && !/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) {
      toast.error("Email inválido");
      return;
    }
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { z_cert_email_to: trimmed });
      setSettings(s);
      setOverride(s.override_to || "");
      toast.success(trimmed ? `Certificados dirigidos a ${trimmed}` : "Destinatario restablecido por defecto");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const toggleAnnualBook = async (next) => {
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { annual_book_auto_email_enabled: next });
      setSettings(s);
      toast.success(next
        ? "Libro anual automático activado · se enviará el 5 de enero"
        : "Libro anual automático desactivado");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const saveBookOverride = async () => {
    const trimmed = bookOverride.trim();
    if (trimmed && !/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) {
      toast.error("Email inválido");
      return;
    }
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { annual_book_email_to: trimmed });
      setSettings(s);
      setBookOverride(s.annual_book?.override_to || "");
      toast.success(trimmed ? `Libro anual dirigido a ${trimmed}` : "Destinatario del libro restablecido");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const sendBookNow = async () => {
    setSendingBook(true);
    try {
      const year = new Date().getFullYear() - 1; // ejercicio anterior (típico uso manual)
      const r = await api.sendAnnualZBook(restaurantId, { year });
      toast.success(`Libro ${year} enviado a ${r.to}`);
    } catch (e) {
      toast.error(e?.message || "Error al enviar el libro");
    }
    finally { setSendingBook(false); }
  };

  const toggleMonthly = async (next) => {
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { monthly_exec_summary_enabled: next });
      setSettings(s);
      toast.success(next
        ? "Resumen mensual activado · llegará el día 1 de cada mes"
        : "Resumen mensual desactivado");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const saveMonthlyOverride = async () => {
    const trimmed = monthlyOverride.trim();
    if (trimmed && !/^[^@]+@[^@]+\.[^@]+$/.test(trimmed)) {
      toast.error("Email inválido");
      return;
    }
    setSaving(true);
    try {
      const s = await api.updateZCertEmailSettings(restaurantId, { monthly_exec_summary_email_to: trimmed });
      setSettings(s);
      setMonthlyOverride(s.monthly_exec_summary?.override_to || "");
      toast.success(trimmed ? `Resumen mensual dirigido a ${trimmed}` : "Destinatario restablecido");
    } catch (e) { toast.error(e?.message || "Error"); }
    finally { setSaving(false); }
  };

  const sendMonthlyNow = async () => {
    setSendingMonthly(true);
    try {
      const r = await api.sendMonthlyExecSummary(restaurantId, {});
      toast.success(`Resumen ${r.period} enviado a ${r.to}`);
    } catch (e) {
      toast.error(e?.message || "Error al enviar el resumen");
    }
    finally { setSendingMonthly(false); }
  };

  if (loading) {
    return (
      <Card data-testid="z-cert-email-card">
        <CardContent className="py-6 text-center text-[11px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Cargando…
        </CardContent>
      </Card>
    );
  }

  const enabled = settings?.enabled !== false;
  const effective = settings?.effective_to;

  return (
    <Card data-testid="z-cert-email-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          Certificado fiscal por email
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Toggle */}
        <div className="flex items-center justify-between gap-3 p-2 rounded border bg-card">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-violet-600" />
              Envío automático tras cada Corte-Z
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              El cumplimiento llega solo a tu bandeja: JSON firmado con SHA-256, reverificable por cualquier auditor.
            </div>
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={toggle}
            disabled={saving}
            data-testid="z-cert-email-toggle"
          />
        </div>

        {/* Destinatario efectivo */}
        <div className="rounded border bg-muted/50 p-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Destinatario efectivo
          </div>
          {effective ? (
            <div className="text-xs font-mono break-all" data-testid="z-cert-email-effective">{effective}</div>
          ) : (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
              <AlertCircle className="h-3.5 w-3.5" />
              No hay destinatario configurado. El certificado no se enviará hasta que rellenes el campo abajo o asignes un owner con email.
            </div>
          )}
        </div>

        {/* Override */}
        <div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
            Redirigir a otro email (opcional)
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder={settings?.owner_email || "contabilidad@empresa.com"}
              className="h-8 text-xs"
              disabled={saving || !enabled}
              data-testid="z-cert-email-override-input"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={saveOverride}
              disabled={saving || !enabled}
              data-testid="z-cert-email-override-save"
            >
              Guardar
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Déjalo vacío para usar el email del propietario por defecto.
          </p>
        </div>

        {/* ─── Libro de Cortes-Z anual (opt-in) ──────────────────── */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between gap-3 p-2 rounded border bg-card">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium flex items-center gap-1.5">
                <BookOpen className="h-3.5 w-3.5 text-emerald-700" />
                Libro anual automático
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Cada 5 de enero, te enviamos el libro del ejercicio anterior (PDF con cadena SHA-256 completa, listo para Hacienda).
              </div>
            </div>
            <Switch
              checked={settings?.annual_book?.enabled === true}
              onCheckedChange={toggleAnnualBook}
              disabled={saving}
              data-testid="annual-book-auto-toggle"
            />
          </div>

          {/* Destinatario del libro */}
          <div className="rounded border bg-muted/50 p-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Destinatario del libro
            </div>
            {settings?.annual_book?.effective_to ? (
              <div className="text-xs font-mono break-all" data-testid="annual-book-effective">
                {settings.annual_book.effective_to}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                <AlertCircle className="h-3.5 w-3.5" />
                Sin destinatario. Añade uno abajo o asigna un propietario con email.
              </div>
            )}
          </div>

          {/* Override del libro */}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Email específico para el libro (opcional)
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="email"
                value={bookOverride}
                onChange={(e) => setBookOverride(e.target.value)}
                placeholder={settings?.owner_email || "libros@contable.com"}
                className="h-8 text-xs"
                disabled={saving}
                data-testid="annual-book-override-input"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={saveBookOverride}
                disabled={saving}
                data-testid="annual-book-override-save"
              >
                Guardar
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Si lo dejas vacío, usa el email del certificado fiscal o el del propietario.
            </p>
          </div>

          {/* Enviar ahora (manual, bypass dedupe) */}
          <Button
            size="sm"
            variant="outline"
            className="w-full h-8 gap-1.5"
            onClick={sendBookNow}
            disabled={sendingBook || !settings?.annual_book?.effective_to}
            data-testid="annual-book-send-now"
          >
            {sendingBook ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            Enviar ahora el libro del ejercicio anterior
          </Button>
        </div>

        {/* ─── Resumen mensual express (opt-in, BI channel) ──────── */}
        <div className="border-t pt-3 space-y-2">
          <div className="flex items-center justify-between gap-3 p-2 rounded border bg-card">
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5 text-blue-600" />
                Resumen mensual express
              </div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                El día 1 de cada mes, te llega un mini-informe con ventas, ticket medio, variación YoY y mejores días — sin abrir el dashboard.
              </div>
            </div>
            <Switch
              checked={settings?.monthly_exec_summary?.enabled === true}
              onCheckedChange={toggleMonthly}
              disabled={saving}
              data-testid="monthly-summary-toggle"
            />
          </div>

          <div className="rounded border bg-muted/50 p-2">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Destinatario del resumen
            </div>
            {settings?.monthly_exec_summary?.effective_to ? (
              <div className="text-xs font-mono break-all" data-testid="monthly-summary-effective">
                {settings.monthly_exec_summary.effective_to}
              </div>
            ) : (
              <div className="flex items-center gap-1.5 text-[11px] text-amber-700">
                <AlertCircle className="h-3.5 w-3.5" />
                Sin destinatario. Añade uno abajo.
              </div>
            )}
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
              Email específico para el resumen (opcional)
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="email"
                value={monthlyOverride}
                onChange={(e) => setMonthlyOverride(e.target.value)}
                placeholder={settings?.owner_email || "kpis@empresa.com"}
                className="h-8 text-xs"
                disabled={saving}
                data-testid="monthly-summary-override-input"
              />
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={saveMonthlyOverride}
                disabled={saving}
                data-testid="monthly-summary-override-save"
              >
                Guardar
              </Button>
            </div>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="w-full h-8 gap-1.5"
            onClick={sendMonthlyNow}
            disabled={sendingMonthly || !settings?.monthly_exec_summary?.effective_to}
            data-testid="monthly-summary-send-now"
          >
            {sendingMonthly ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Send className="h-3 w-3" />
            )}
            Enviar ahora el resumen del mes anterior
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
